import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Swal from 'sweetalert2'
import { Alert, Button, Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap'
import { getRewardsAdminErrorMessage, updateRewardLevel } from '../../../../helpers/api/rewardsAdmin'
import { Toast } from '../../../UI/toast/Toast'
import { formatMultiplier, formatNumber } from '../../../rewards/rewardsUtils'

const toNumber = (text) => {
  const number = Number(String(text).replace(',', '.'))
  return String(text).trim() === '' || !Number.isFinite(number) ? null : number
}

const validate = ({ minXp, maxXp, coinMultiplier }) => {
  const errors = {}
  const min = toNumber(minXp)
  const max = String(maxXp).trim() === '' ? null : toNumber(maxXp)
  const multiplier = toNumber(coinMultiplier)
  if (min === null || min < 0) errors.minXp = 'Ingresa un XP mínimo de 0 o más.'
  if (String(maxXp).trim() !== '' && (max === null || max < 0)) errors.maxXp = 'Ingresa un número o déjalo vacío.'
  else if (min !== null && max !== null && max < min) errors.maxXp = 'No puede ser menor que el XP mínimo.'
  if (multiplier === null || multiplier < 1) errors.coinMultiplier = 'El multiplicador no puede ser menor que 1.'
  return errors
}

// HO-TASK-289: rango de XP y multiplicador de Coins de un nivel. A diferencia
// de los parámetros, el nivel se edita en el momento (sin vigencia), así que
// el cambio aplica a los próximos pagos.
const LevelEditModal = ({ level, onClose, onSaved }) => {
  const [form, setForm] = useState({ minXp: '', maxXp: '', coinMultiplier: '', note: '' })
  const [saving, setSaving] = useState(false)
  const [serverError, setServerError] = useState(null)

  useEffect(() => {
    if (!level) return
    setForm({
      minXp: String(level.min_xp),
      maxXp: level.max_xp === null ? '' : String(level.max_xp),
      coinMultiplier: String(level.coin_multiplier),
      note: ''
    })
    setServerError(null)
  }, [level])

  const errors = validate(form)
  const hasErrors = Object.keys(errors).length > 0
  const update = (field) => ({ target }) => setForm((current) => ({ ...current, [field]: target.value }))

  const save = async () => {
    if (hasErrors) return
    const maxXp = form.maxXp.trim() === '' ? null : toNumber(form.maxXp)
    const confirmation = await Swal.fire({
      title: `¿Guardar el nivel ${level.name}?`,
      html:
        `XP de <b>${formatNumber(toNumber(form.minXp))}</b> a <b>${maxXp === null ? 'sin tope' : formatNumber(maxXp)}</b> ` +
        `y multiplicador <b>${formatMultiplier(toNumber(form.coinMultiplier))}</b>.<br/>Aplica a los próximos pagos.`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#9341af',
      cancelButtonColor: '#bbb',
      confirmButtonText: 'Sí, guardar',
      cancelButtonText: 'Cancelar'
    })
    if (!confirmation.value) return

    setSaving(true)
    setServerError(null)
    try {
      await updateRewardLevel(level.id, {
        minXp: toNumber(form.minXp),
        maxXp,
        coinMultiplier: toNumber(form.coinMultiplier),
        note: form.note.trim()
      })
      Toast.fire({ type: 'success', title: 'Nivel guardado.' })
      onSaved()
    } catch (error) {
      setServerError(getRewardsAdminErrorMessage(error, 'No pudimos guardar el nivel.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={!!level} toggle={saving ? undefined : onClose} centered>
      <ModalHeader toggle={saving ? undefined : onClose}>Nivel {level?.name}</ModalHeader>
      <ModalBody>
        {serverError && <Alert color="danger" className="fs--1">{serverError}</Alert>}
        <Row form>
          <Col xs={6}>
            <FormGroup>
              <Label for="level-min-xp" className="fs--1">XP mínimo</Label>
              <Input id="level-min-xp" type="number" min={0} bsSize="sm" value={form.minXp} invalid={!!errors.minXp} onChange={update('minXp')} />
              <FormFeedback>{errors.minXp}</FormFeedback>
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup>
              <Label for="level-max-xp" className="fs--1">XP máximo</Label>
              <Input
                id="level-max-xp"
                type="number"
                min={0}
                bsSize="sm"
                value={form.maxXp}
                placeholder="Sin tope"
                invalid={!!errors.maxXp}
                onChange={update('maxXp')}
              />
              <FormFeedback>{errors.maxXp}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="level-multiplier" className="fs--1">Multiplicador de Coins</Label>
          <Input
            id="level-multiplier"
            type="number"
            min={1}
            step="0.01"
            bsSize="sm"
            value={form.coinMultiplier}
            invalid={!!errors.coinMultiplier}
            onChange={update('coinMultiplier')}
          />
          <FormFeedback>{errors.coinMultiplier}</FormFeedback>
          <small className="text-600">1,25 significa que el nivel gana 25 % más Coins por pago.</small>
        </FormGroup>
        <FormGroup className="mb-0">
          <Label for="level-note" className="fs--1">Motivo del cambio (opcional)</Label>
          <Input id="level-note" type="textarea" rows={2} maxLength={255} bsSize="sm" value={form.note} onChange={update('note')} />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="falcon-default" size="sm" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={save} disabled={hasErrors || saving}>
          {saving && <Spinner size="sm" className="mr-2" />}
          Guardar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

LevelEditModal.propTypes = { level: PropTypes.object, onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired }

export default LevelEditModal
